import { useEffect, useState, useCallback } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  Chip,
  Snackbar,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useDispatch, useSelector } from "react-redux";
import {
  getComments,
  getCommentCounts,
  addComment,
  editComment,
  toggleResolveComment,
  removeComment,
} from "../../actions/commentActions";
import CommentThread from "./CommentThread";
import CommentInput from "./CommentInput";
import { usePermission } from "../../pages/authentication/permission-providor/UserPermission";

// Right-side drawer listing comment threads for an entry. When `fieldAnchor`
// is set ({ fieldId, fieldName }) the drawer is scoped to that field and new
// root comments anchor to it; otherwise new comments are entry-level.
const CommentsDrawer = ({
  open,
  onClose,
  workspaceId,
  entryId,
  contentTypeId,
  fieldAnchor = null,
}) => {
  const dispatch = useDispatch();
  const [filter, setFilter] = useState('open');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const { comments, loading } = useSelector((state) => state.comments);
  const { isAdmin } = usePermission();

  const currentUser = JSON.parse(localStorage.getItem('userInfo') || 'null');
  const currentUserId = currentUser?._id;

  const refresh = useCallback(() => {
    if (workspaceId && entryId) {
      dispatch(getComments(workspaceId, entryId, filter, fieldAnchor?.fieldId));
      dispatch(getCommentCounts(workspaceId, entryId));
    }
  }, [dispatch, workspaceId, entryId, filter, fieldAnchor?.fieldId]);

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  const notify = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  const handleResult = (result, successMessage) => {
    if (result?.status === 'success') {
      if (successMessage) notify(successMessage);
      refresh();
      return true;
    }
    notify(result?.message || 'Something went wrong', 'error');
    return false;
  };

  const handleCreateRoot = async (body) => {
    const result = await dispatch(
      addComment(workspaceId, entryId, {
        body,
        contentTypeId: String(contentTypeId || ''),
        anchorType: fieldAnchor ? 'field' : 'entry',
        ...(fieldAnchor
          ? { fieldId: String(fieldAnchor.fieldId), fieldName: fieldAnchor.fieldName }
          : {}),
      })
    );
    return handleResult(result);
  };

  const handleReply = async (root, body) => {
    const result = await dispatch(
      addComment(workspaceId, entryId, {
        body,
        contentTypeId: String(contentTypeId || ''),
        parentId: root._id,
        anchorType: root.anchor?.type || 'entry',
        ...(root.anchor?.type === 'field'
          ? { fieldId: root.anchor.fieldId, fieldName: root.anchor.fieldName }
          : {}),
      })
    );
    return handleResult(result);
  };

  const handleEdit = async (comment, body) => {
    const result = await dispatch(editComment(comment._id, body));
    return handleResult(result);
  };

  const handleToggleResolve = async (root) => {
    const result = await dispatch(toggleResolveComment(root._id, !root.resolved));
    return handleResult(result, root.resolved ? 'Comment reopened' : 'Comment resolved');
  };

  const handleDelete = async (comment) => {
    const result = await dispatch(removeComment(comment._id));
    return handleResult(result, 'Comment deleted');
  };

  const roots = comments.filter((c) => !c.parentId);
  const repliesByRoot = comments.reduce((acc, c) => {
    if (c.parentId) {
      (acc[c.parentId] = acc[c.parentId] || []).push(c);
    }
    return acc;
  }, {});

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 420,
          bgcolor: '#FAFAFA',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box
          sx={{
            p: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant="h6" sx={{ flex: 1 }}>
            Comments
          </Typography>
          {fieldAnchor && (
            <Chip
              label={fieldAnchor.fieldName || fieldAnchor.fieldId}
              size="small"
              variant="outlined"
            />
          )}
          <Tooltip title="Refresh">
            <IconButton onClick={refresh} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ px: 2, pt: 1.5 }}>
          <ToggleButtonGroup
            value={filter}
            exclusive
            size="small"
            onChange={(e, next) => next && setFilter(next)}
          >
            <ToggleButton value="open">Open</ToggleButton>
            <ToggleButton value="resolved">Resolved</ToggleButton>
            <ToggleButton value="all">All</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box sx={{ p: 2, overflowY: 'auto', flex: 1 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : roots.length > 0 ? (
            roots.map((root) => (
              <CommentThread
                key={root._id}
                root={root}
                replies={repliesByRoot[root._id] || []}
                currentUserId={currentUserId}
                isAdmin={isAdmin}
                onReply={handleReply}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onToggleResolve={handleToggleResolve}
              />
            ))
          ) : (
            <Box
              sx={{
                p: 3,
                border: '1px dashed',
                borderColor: 'divider',
                borderRadius: 1,
                textAlign: 'center',
                bgcolor: 'background.paper',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No {filter !== 'all' ? filter : ''} comments yet.
              </Typography>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            p: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <CommentInput
            onSubmit={handleCreateRoot}
            placeholder={
              fieldAnchor
                ? `Comment on "${fieldAnchor.fieldName || fieldAnchor.fieldId}"… use @ to mention`
                : 'Add a comment… use @ to mention'
            }
          />
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Drawer>
  );
};

export default CommentsDrawer;
